import React, { FC, useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  InputAdornment,
  Icon,
  Autocomplete,
  CircularProgress,
  Stack,
  Box,
  Typography,
  Tooltip,
  Chip,
  Card,
  CardContent,
  LinearProgress,
  alpha,
  useTheme,
  Collapse,
  IconButton,
  Divider,
} from "@mui/material";
import Timeline from "@mui/lab/Timeline";
import TimelineItem from "@mui/lab/TimelineItem";
import TimelineSeparator from "@mui/lab/TimelineSeparator";
import TimelineDot from "@mui/lab/TimelineDot";
import TimelineConnector from "@mui/lab/TimelineConnector";
import TimelineContent from "@mui/lab/TimelineContent";
import { IngredientOption, ExtendedBatch } from "../types";
import dayjs from "dayjs";
import { UnitSelect } from "../../../../components/UnitSelect/UnitSelect";
import { convertUnits, formatUnitValue } from "../utils/units";
import { CompactUnitSelect } from "./CompactUnitSelect";
import { AutocompleteRenderOptionState } from "@mui/material/Autocomplete";
import { useNavigate } from "react-router-dom";

interface IngredientEntry {
  ingredient: IngredientOption | null;
  quantity: number;
  unit: string;
  notes: string;
  selectedBatch?: ExtendedBatch;
  isExpanded: boolean;
  batches: ExtendedBatch[];
  loadingBatches: boolean;
  error?: string;
}

interface IngredientDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (
    data: Array<{
      ingredient: IngredientOption;
      quantity: number;
      unit: string;
      notes: string;
      selectedBatch?: ExtendedBatch;
    }>
  ) => void;
  ingredientOptions: IngredientOption[];
  loadingIngredients: boolean;
  loadBatches: (ingredientId: string) => Promise<{
    batches: ExtendedBatch[];
    totalCost: number;
    averageCost: number;
    totalQuantity: number;
  } | null>;
  mode: "add" | "edit";
  currentRecipeIngredients: Array<{
    item_id: string;
    item_type: string;
    quantity: number;
    unit: string;
  }>;
  editData?: {
    ingredient: IngredientOption;
    quantity: number;
    unit: string;
    notes: string;
    selectedBatch?: ExtendedBatch;
  };
}

// Add theme colors at the top after imports
const THEME_COLORS = {
  primary: '#0891B2',  // Cyan-600
  primaryHover: '#0E7490', // Cyan-700
  secondary: '#64748B', // Slate-500
  danger: '#DC2626', // Red-600
  dangerHover: '#B91C1C', // Red-700
  success: '#059669', // Emerald-600
  background: {
    main: '#FFFFFF',
    light: '#F8FAFC',
    card: '#FFFFFF',
  },
  border: {
    light: alpha('#0891B2', 0.1),
    main: alpha('#0891B2', 0.2),
  },
  text: {
    primary: '#1E293B', // Slate-800
    secondary: '#64748B', // Slate-500
    light: '#94A3B8', // Slate-400
  }
};

export const IngredientDialog: FC<IngredientDialogProps> = ({
  open,
  onClose,
  onSave,
  ingredientOptions,
  loadingIngredients,
  loadBatches,
  mode = "add",
  currentRecipeIngredients,
  editData,
}) => {
  const theme = useTheme();
  const [ingredients, setIngredients] = useState<IngredientEntry[]>([]);
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();

  // Filter out already selected ingredients
  const availableIngredients = React.useMemo(() => {
    return ingredientOptions?.filter((option) => {
      if (mode === "edit" && editData?.ingredient.id === option.id) {
        return true;
      }
      return !currentRecipeIngredients?.some(
        (ing) => ing.item_id === option.id
      );
    });
  }, [ingredientOptions, mode, editData, currentRecipeIngredients]);

  useEffect(() => {
    if (open) {
      if (mode === "edit" && editData) {
        setIngredients([{
          ...editData,
          isExpanded: false,
          batches: [],
          loadingBatches: false
        }]);
      } else {
        setIngredients([
          {
            ingredient: {
              id: "",
              _id: "",
              ingredient_id: "",
              type: "ingredient",
              name: "",
              unit: "",
            },
            quantity: 0,
            unit: "",
            notes: "",
            batches: [],
            loadingBatches: false,
            isExpanded: false,
          },
        ]);
      }
    }
  }, [open, mode, editData]);

  const handleAddIngredient = () => {
    setIngredients([
      ...ingredients,
      {
        ingredient: null,
        quantity: 0,
        unit: "",
        notes: "",
        isExpanded: false,
        batches: [],
        loadingBatches: false,
      },
    ]);
  };

  const handleRemoveIngredient = (index: number) => {
    setIngredients(ingredients?.filter((_, i) => i !== index));
  };

  const loadIngredientBatches = async (index: number, ingredientId: string) => {
    setIngredients((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], loadingBatches: true };
      return updated;
    });

    try {
      const batchInfo = await loadBatches(ingredientId);
      if (batchInfo) {
        // Sort batches by expiry date
        const sortedBatches = [...batchInfo.batches].sort((a, b) =>
          dayjs(a.expiry_date).diff(dayjs(b.expiry_date))
        );

        // Find the first non-expired batch with sufficient quantity
        const recommendedBatch = sortedBatches.find(
          (batch) =>
            dayjs(batch.expiry_date).isAfter(dayjs()) &&
            batch.remaining_quantity >= (ingredients[index].quantity || 0)
        );

        setIngredients((prev) => {
          const updated = [...prev];
          updated[index] = {
            ...updated[index],
            batches: sortedBatches,
            selectedBatch: recommendedBatch || undefined,
            loadingBatches: false,
            isExpanded: true,
          };
          return updated;
        });
      }
    } catch (error) {
      setIngredients((prev) => {
        const updated = [...prev];
        updated[index] = {
          ...updated[index],
          loadingBatches: false,
          error: "Failed to load batches",
        };
        return updated;
      });
    }
  };

  const handleIngredientChange = (
    index: number,
    ingredient: IngredientOption | null
  ) => {
    if (ingredient) {
      setIngredients((prev) => {
        const updated = [...prev];
        updated[index] = {
          ...updated[index],
          ingredient,
          unit: ingredient.unit,
          selectedBatch: undefined,
        };
        return updated;
      });
      loadIngredientBatches(index, ingredient.ingredient_id);
    }
  };

  const handleQuantityChange = (index: number, value: number) => {
    setIngredients((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], quantity: value };
      return updated;
    });
  };

  const handleUnitChange = (index: number, unit: string) => {
    setIngredients((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], unit };
      return updated;
    });
  };

  const handleNotesChange = (index: number, notes: string) => {
    setIngredients((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], notes };
      return updated;
    });
  };

  const handleBatchSelect = (index: number, batch: ExtendedBatch) => {
    setIngredients((prev) => {
      const updated = [...prev];
      updated[index] = {
        ...updated[index],
        selectedBatch: batch,
        isExpanded: false,
      };
      return updated;
    });
  };

  const handleToggleExpand = (index: number) => {
    setIngredients((prev) => {
      const updated = [...prev];
      updated[index] = {
        ...updated[index],
        isExpanded: !updated[index].isExpanded,
      };
      return updated;
    });
  };

  const handleSave = () => {
    const validIngredients = ingredients
      ?.filter((entry) => entry.ingredient && entry.quantity > 0)
      ?.map((entry) => ({
        ingredient: entry.ingredient!,
        quantity: entry.quantity,
        unit: entry.unit,
        notes: entry.notes,
        selectedBatch: entry.selectedBatch,
      }));

    onSave(validIngredients);
    onClose();
  };

  const isValid = ingredients?.some(
    (entry) => entry.ingredient && entry.quantity > 0
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          bgcolor: THEME_COLORS.background.light,
          maxHeight: "90vh",
        },
      }}
    >
      <DialogTitle
        sx={{
          borderBottom: `1px solid ${THEME_COLORS.border.light}`,
          px: 3,
          py: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          bgcolor: THEME_COLORS.background.main,
        }}
      >
        <Box>
          <Typography 
            variant="h6" 
            sx={{ 
              fontFamily: 'Montserrat',
              fontWeight: 600,
              color: THEME_COLORS.text.primary,
              mb: 0.5,
            }}
          >
            {mode === "edit" ? "Edit Ingredient" : "Add Ingredients"}
          </Typography>
          <Typography 
            variant="body2" 
            sx={{ color: THEME_COLORS.text.secondary }}
          >
            {mode === "edit"
              ? "Modify the ingredient details and batch selection"
              : "Search and add ingredients to your recipe with their quantities and batches"}
          </Typography>
        </Box>
        {mode === "add" && ingredients.length < ingredientOptions.length && (
          <Button
            startIcon={<Icon>add</Icon>}
            onClick={handleAddIngredient}
            variant="contained"
            sx={{
              bgcolor: THEME_COLORS.primary,
              '&:hover': {
                bgcolor: THEME_COLORS.primaryHover,
              },
            }}
          >
            Add Another
          </Button>
        )}
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        {loadingIngredients ? (
          <Box sx={{ p: 4, textAlign: "center" }}>
            <CircularProgress sx={{ color: THEME_COLORS.primary }} />
          </Box>
        ) : availableIngredients.length === 0 ? (
          <Box sx={{ p: 4, textAlign: "center" }}>
            <Icon 
              sx={{ 
                fontSize: 48, 
                color: alpha(THEME_COLORS.primary, 0.2),
                mb: 2 
              }}
            >
              restaurant
            </Icon>
            <Typography 
              variant="h6" 
              gutterBottom
              sx={{ 
                color: THEME_COLORS.text.primary,
                fontFamily: 'Montserrat',
                fontWeight: 600,
              }}
            >
              No Ingredients Available
            </Typography>
            <Typography 
              variant="body2" 
              sx={{ color: THEME_COLORS.text.secondary }}
            >
              All ingredients have been added or no ingredients exist.
            </Typography>
          </Box>
        ) : (
          <Stack spacing={3}>
            {ingredients.map((entry, index) => (
              <Card
                key={index}
                sx={{
                  bgcolor: THEME_COLORS.background.light,
                  border: `1px solid ${THEME_COLORS.border.light}`,
                  borderRadius: 2,
                  transition: "all 0.2s ease-in-out",
                  "&:hover": {
                    borderColor: THEME_COLORS.primary,
                    boxShadow: `0 0 0 1px ${THEME_COLORS.primary}`,
                  },
                }}
              >
                <CardContent sx={{ p: "16px !important" }}>
                  <Stack spacing={2}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Autocomplete
                        value={entry.ingredient}
                        onChange={(_e, newValue) =>
                          handleIngredientChange(index, newValue)
                        }
                        options={availableIngredients}
                        getOptionLabel={(option) => option.name}
                        fullWidth
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Select Ingredient"
                            required
                            size="small"
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                  borderColor: THEME_COLORS.primary,
                                },
                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                  borderColor: THEME_COLORS.primary,
                                }
                              }
                            }}
                          />
                        )}
                      />
                      {mode === "add" && ingredients.length > 1 && (
                        <IconButton
                          size="small"
                          onClick={() => handleRemoveIngredient(index)}
                          sx={{
                            color: THEME_COLORS.danger,
                            "&:hover": {
                              bgcolor: alpha(THEME_COLORS.danger, 0.08),
                            },
                          }}
                        >
                          <Icon fontSize="small">delete</Icon>
                        </IconButton>
                      )}
                    </Box>

                    {entry.ingredient && (
                      <>
                        <Stack direction="row" spacing={2}>
                          <TextField
                            fullWidth
                            size="small"
                            label="Quantity"
                            type="number"
                            value={entry.quantity || ""}
                            onChange={(e) =>
                              handleQuantityChange(index, Number(e.target.value))
                            }
                            required
                            inputProps={{ step: "0.0001", min: 0 }}
                            InputProps={{
                              endAdornment: (
                                <UnitSelect
                                  value={entry.unit}
                                  onChange={(value) => handleUnitChange(index, value)}
                                  sourceUnit={entry.ingredient.unit}
                                />
                              ),
                            }}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                  borderColor: THEME_COLORS.primary,
                                },
                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                  borderColor: THEME_COLORS.primary,
                                }
                              }
                            }}
                          />
                        </Stack>

                        <TextField
                          fullWidth
                          size="small"
                          label="Notes"
                          multiline
                          rows={2}
                          value={entry.notes}
                          onChange={(e) =>
                            handleNotesChange(index, e.target.value)
                          }
                          placeholder="Add any special instructions or notes"
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              '&:hover .MuiOutlinedInput-notchedOutline': {
                                borderColor: THEME_COLORS.primary,
                              },
                              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                borderColor: THEME_COLORS.primary,
                              }
                            }
                          }}
                        />

                        {entry.batches.length > 0 && (
                          <Box>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                mb: 1,
                              }}
                            >
                              <Typography 
                                variant="subtitle2"
                                sx={{ 
                                  color: THEME_COLORS.text.primary,
                                  fontFamily: 'Montserrat',
                                  fontWeight: 600,
                                }}
                              >
                                Available Batches
                              </Typography>
                              <IconButton
                                size="small"
                                onClick={() => handleToggleExpand(index)}
                                sx={{
                                  transform: entry.isExpanded ? "rotate(180deg)" : "none",
                                  transition: "transform 0.2s",
                                  color: THEME_COLORS.primary,
                                }}
                              >
                                <Icon>expand_more</Icon>
                              </IconButton>
                            </Box>

                            <Collapse in={entry.isExpanded}>
                              <Timeline
                                sx={{
                                  p: 0,
                                  m: 0,
                                  "& .MuiTimelineItem-root": {
                                    "&:before": {
                                      flex: 0,
                                      padding: 0,
                                    },
                                  },
                                }}
                              >
                                {entry.batches.map((batch) => {
                                  const isExpired = dayjs(batch.expiry_date).isBefore(dayjs());
                                  const isExpiring = dayjs(batch.expiry_date).isBefore(
                                    dayjs().add(30, "day")
                                  );
                                  const isSelected = entry.selectedBatch?.batch_id === batch.batch_id;

                                  return (
                                    <TimelineItem key={batch.batch_id}>
                                      <TimelineSeparator>
                                        <TimelineDot
                                          sx={{
                                            bgcolor: isSelected
                                              ? THEME_COLORS.primary
                                              : isExpired
                                              ? THEME_COLORS.danger
                                              : isExpiring
                                              ? THEME_COLORS.secondary
                                              : THEME_COLORS.success,
                                          }}
                                        >
                                          <Icon fontSize="small">
                                            {isSelected
                                              ? "check"
                                              : isExpired
                                              ? "error"
                                              : isExpiring
                                              ? "warning"
                                              : "inventory"}
                                          </Icon>
                                        </TimelineDot>
                                        <TimelineConnector />
                                      </TimelineSeparator>
                                      <TimelineContent>
                                        <Card
                                          sx={{
                                            bgcolor: THEME_COLORS.background.main,
                                            border: `1px solid ${isSelected 
                                              ? THEME_COLORS.primary 
                                              : THEME_COLORS.border.light}`,
                                            borderRadius: 1,
                                            cursor: "pointer",
                                            transition: "all 0.2s",
                                            "&:hover": {
                                              borderColor: THEME_COLORS.primary,
                                              bgcolor: alpha(THEME_COLORS.primary, 0.04),
                                            },
                                          }}
                                          onClick={() => handleBatchSelect(index, batch)}
                                        >
                                          <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                                            <Stack spacing={1}>
                                              <Box
                                                sx={{
                                                  display: "flex",
                                                  alignItems: "center",
                                                  justifyContent: "space-between",
                                                }}
                                              >
                                                <Typography
                                                  variant="subtitle2"
                                                  sx={{ 
                                                    color: THEME_COLORS.text.primary,
                                                    fontWeight: 500,
                                                  }}
                                                >
                                                  Batch #{batch.batch_number}
                                                </Typography>
                                                <Typography
                                                  variant="caption"
                                                  sx={{ color: THEME_COLORS.text.secondary }}
                                                >
                                                  {dayjs(batch.expiry_date).format("MMM D, YYYY")}
                                                </Typography>
                                              </Box>
                                              <Box
                                                sx={{
                                                  display: "flex",
                                                  alignItems: "center",
                                                  gap: 1,
                                                }}
                                              >
                                                <Typography
                                                  variant="body2"
                                                  sx={{ color: THEME_COLORS.text.secondary }}
                                                >
                                                  Available:
                                                </Typography>
                                                <Typography
                                                  variant="body2"
                                                  sx={{ 
                                                    color: THEME_COLORS.text.primary,
                                                    fontWeight: 500,
                                                  }}
                                                >
                                                  {batch.remaining_quantity} {batch.unit}
                                                </Typography>
                                              </Box>
                                            </Stack>
                                          </CardContent>
                                        </Card>
                                      </TimelineContent>
                                    </TimelineItem>
                                  );
                                })}
                              </Timeline>
                            </Collapse>
                          </Box>
                        )}
                      </>
                    )}
                  </Stack>
                </CardContent>
              </Card>
            ))}
          </Stack>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          borderTop: `1px solid ${THEME_COLORS.border.light}`,
          px: 3,
          py: 2,
          bgcolor: THEME_COLORS.background.main,
        }}
      >
        <Button 
          onClick={onClose}
          sx={{
            color: THEME_COLORS.text.primary,
            '&:hover': {
              bgcolor: alpha(THEME_COLORS.primary, 0.04),
            },
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={!isValid}
          startIcon={<Icon>save</Icon>}
          sx={{
            bgcolor: THEME_COLORS.primary,
            '&:hover': {
              bgcolor: THEME_COLORS.primaryHover,
            },
            '&.Mui-disabled': {
              bgcolor: alpha(THEME_COLORS.primary, 0.12),
              color: alpha(THEME_COLORS.primary, 0.26),
            },
          }}
        >
          {mode === "edit" ? "Save Changes" : "Add Selected Items"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
